import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
	Box,
	Paper,
	Typography,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TablePagination,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	TextField,
	IconButton,
	Button,
	Alert,
	Grid,
	Chip,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	Tooltip,
	CircularProgress,
	Stack,
	Collapse,
	ButtonGroup,
	Snackbar,
	Avatar,
	Autocomplete,
} from "@mui/material";
import {
	Check as CheckIcon,
	X as XIcon,
	Search as SearchIcon,
	FileText as ReceiptIcon,
	Download as DownloadIcon,
	MessageCircle as MessageIcon,
	Send as SendIcon,
	RefreshCcw as RefreshIcon,
} from "lucide-react";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { useAuth } from "../../contexts/AuthContext";
import dayjs from "dayjs";

const ExpenseApprovals = () => {
	const { supabase, user } = useAuth();
	const [pendingExpenses, setPendingExpenses] = useState([]);
	const [selectedExpenses, setSelectedExpenses] = useState(new Set());
	const [selectedExpense, setSelectedExpense] = useState(null);
	const [openReceiptDialog, setOpenReceiptDialog] = useState(false);
	const [openMessageDialog, setOpenMessageDialog] = useState(false);
	const [messageForm, setMessageForm] = useState({
		subject: "",
		message: "",
	});
	const [sendingMessage, setSendingMessage] = useState(false);
	const [processingExpenses, setProcessingExpenses] = useState(new Set());
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(25);
	const [loading, setLoading] = useState(true);
	const [refreshing, setRefreshing] = useState(false);
	const [showFilters, setShowFilters] = useState(false);

	// Notifications
	const [notification, setNotification] = useState({
		open: false,
		message: "",
		severity: "success",
	});

	const [expenseTypes, setExpenseTypes] = useState([]);

	// Filters
	const [filters, setFilters] = useState({
		searchTerm: "",
		expenseType: "",
		startDate: null,
		endDate: null,
		user: "",
	});
	const [users, setUsers] = useState([]);

	// Memoized filtered expenses
	const filteredExpenses = useMemo(() => {
		let filtered = [...pendingExpenses];

		if (filters.searchTerm) {
			const searchLower = filters.searchTerm.toLowerCase();
			filtered = filtered.filter(
				(expense) =>
					expense.description?.toLowerCase().includes(searchLower) ||
					expense.profiles?.full_name?.toLowerCase().includes(searchLower) ||
					expense.profiles?.email?.toLowerCase().includes(searchLower)
			);
		}

		if (filters.expenseType) {
			filtered = filtered.filter(
				(expense) => expense.expense_type === filters.expenseType
			);
		}

		if (filters.startDate) {
			filtered = filtered.filter(
				(expense) =>
					dayjs(expense.date).isAfter(filters.startDate) ||
					dayjs(expense.date).isSame(filters.startDate)
			);
		}

		if (filters.endDate) {
			filtered = filtered.filter(
				(expense) =>
					dayjs(expense.date).isBefore(filters.endDate) ||
					dayjs(expense.date).isSame(filters.endDate)
			);
		}

		if (filters.user) {
			filtered = filtered.filter((expense) => expense.user_id === filters.user);
		}

		return filtered;
	}, [filters, pendingExpenses]);

	// Show notification
	const showNotification = useCallback((message, severity = "success") => {
		setNotification({ open: true, message, severity });
	}, []);

	// Hide notification
	const hideNotification = useCallback(() => {
		setNotification((prev) => ({ ...prev, open: false }));
	}, []);

	useEffect(() => {
		fetchPendingExpenses();
		fetchUsers();
		fetchExpenseTypes();
	}, []);

	const fetchExpenseTypes = async () => {
		try {
			const { data, error } = await supabase
				.from("expense_types")
				.select("*")
				.eq("status", true)
				.order("name");

			if (error) throw error;
			setExpenseTypes(data || []);
		} catch (err) {
			console.error("Error fetching expense types:", err);
		}
	};

	const fetchPendingExpenses = async (showRefresh = false) => {
		if (showRefresh) setRefreshing(true);
		else setLoading(true);

		try {
			const { data, error } = await supabase
				.from("expenses")
				.select(
					`
					*,
					profiles:user_id (
						id,
						full_name,
						email
					),
					school_visits (
						id,
						schools (
							id,
							name
						),
						visited_date,
						planned_date
					),
					schools (
						id,
						name
					)
				`
				)
				.eq("status", "Pending Approval")
				.order("created_at", { ascending: false });

			if (error) throw error;
			setPendingExpenses(data || []);

			// Clear selections if expenses no longer exist
			if (data) {
				const currentIds = new Set(data.map((exp) => exp.id));
				setSelectedExpenses(
					(prev) => new Set([...prev].filter((id) => currentIds.has(id)))
				);
			}
		} catch (err) {
			console.error("Error fetching pending expenses:", err);
			showNotification("Error loading pending expenses", "error");
		} finally {
			setLoading(false);
			setRefreshing(false);
		}
	};

	const fetchUsers = async () => {
		try {
			const { data, error } = await supabase
				.from("profiles")
				.select("id, full_name, email, role")
				.neq("role", "ROLE_SCHOOL_TEACHER")
				.order("full_name");

			if (error) throw error;
			setUsers(data || []);
		} catch (err) {
			console.error("Error fetching users:", err);
		}
	};

	const handleExpenseAction = async (expenseIds, status, actionName) => {
		const idsArray = Array.isArray(expenseIds) ? expenseIds : [expenseIds];

		// Add to processing state
		setProcessingExpenses((prev) => new Set([...prev, ...idsArray]));

		try {

			// Update the expenses in the database
			const { data, error } = await supabase
				.from("expenses")
				.update({
					status: status,
					updated_at: new Date().toISOString(),
				})
				.in("id", idsArray)
				.select(); // Add select to return updated rows

			if (error) {
				console.error(`Database error during ${actionName}:`, error);
				throw error;
			}


			const count = idsArray.length;
			const message =
				count === 1
					? `Expense ${actionName.toLowerCase()} successfully`
					: `${count} expenses ${actionName.toLowerCase()} successfully`;

			showNotification(message, "success");

			// Remove from selections
			setSelectedExpenses((prev) => {
				const newSet = new Set(prev);
				idsArray.forEach((id) => newSet.delete(id));
				return newSet;
			});

			// Refresh the expenses list to get updated data
			await fetchPendingExpenses();
		} catch (err) {
			console.error(`Error ${actionName.toLowerCase()} expense:`, err);

			// More detailed error message
			let errorMessage = `Error ${actionName.toLowerCase()} expense(s)`;
			if (err.message) {
				errorMessage += `: ${err.message}`;
			}

			showNotification(errorMessage, "error");
		} finally {
			// Remove from processing state
			setProcessingExpenses((prev) => {
				const newSet = new Set(prev);
				idsArray.forEach((id) => newSet.delete(id));
				return newSet;
			});
		}
	};

	const handleApprove = async (expenseId) => {
		await handleExpenseAction(expenseId, "Approved", "approved");
	};

	const handleReject = async (expenseId) => {
		await handleExpenseAction(expenseId, "Rejected", "rejected");
	};

	const handleBulkApprove = async () => {
		if (selectedExpenses.size === 0) return;
		await handleExpenseAction([...selectedExpenses], "Approved", "approved");
	};

	const handleBulkReject = async () => {
		if (selectedExpenses.size === 0) return;
		await handleExpenseAction([...selectedExpenses], "Rejected", "rejected");
	};

	const toggleExpenseSelection = (expenseId) => {
		setSelectedExpenses((prev) => {
			const newSet = new Set(prev);
			if (newSet.has(expenseId)) {
				newSet.delete(expenseId);
			} else {
				newSet.add(expenseId);
			}
			return newSet;
		});
	};

	const toggleAllExpenses = () => {
		const currentPageExpenses = filteredExpenses
			.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
			.map((exp) => exp.id);

		const allSelected = currentPageExpenses.every((id) =>
			selectedExpenses.has(id)
		);

		setSelectedExpenses((prev) => {
			const newSet = new Set(prev);
			if (allSelected) {
				// Deselect all on current page
				currentPageExpenses.forEach((id) => newSet.delete(id));
			} else {
				// Select all on current page
				currentPageExpenses.forEach((id) => newSet.add(id));
			}
			return newSet;
		});
	};

	const viewReceipt = (expense) => {
		setSelectedExpense(expense);
		setOpenReceiptDialog(true);
	};

	const closeReceiptDialog = () => {
		setSelectedExpense(null);
		setOpenReceiptDialog(false);
	};

	const openMessageModal = (expense) => {
		setSelectedExpense(expense);
		setMessageForm({
			subject: `Regarding your expense: ${expense.description}`,
			message: "",
		});
		setOpenMessageDialog(true);
	};

	const closeMessageModal = () => {
		setSelectedExpense(null);
		setMessageForm({
			subject: "",
			message: "",
		});
		setOpenMessageDialog(false);
	};

	const handleSendMessage = async () => {
		if (!messageForm.subject.trim() || !messageForm.message.trim()) {
			showNotification("Please fill in both subject and message", "error");
			return;
		}

		try {
			setSendingMessage(true);

			const notification = {
				sender_id: user.id,
				recipient_id: selectedExpense.user_id,
				subject: messageForm.subject.trim(),
				message: messageForm.message.trim(),
				type: "expense_inquiry",
				is_read: false,
				created_at: new Date().toISOString(),
			};

			const { error } = await supabase
				.from("notifications")
				.insert(notification);

			if (error) throw error;

			showNotification(
				`Message sent to ${
					selectedExpense.profiles.full_name || selectedExpense.profiles.email
				} successfully`
			);
			closeMessageModal();
		} catch (err) {
			console.error("Error sending message:", err);
			showNotification("Failed to send message. Please try again.", "error");
		} finally {
			setSendingMessage(false);
		}
	};

	const resetFilters = () => {
		setFilters({
			searchTerm: "",
			expenseType: "",
			startDate: null,
			endDate: null,
			user: "",
		});
		setPage(0);
	};

	const handleFilterChange = (key, value) => {
		setFilters({
			...filters,
			[key]: value,
		});
		setPage(0);
	};

	const formatCurrency = (amount) => {
		return new Intl.NumberFormat("en-IN", {
			style: "currency",
			currency: "INR",
			minimumFractionDigits: 0,
			maximumFractionDigits: 0,
		}).format(amount);
	};

	const getExpenseTypeChipColor = (type) => {
		switch (type) {
			case "Travel Expense":
				return "primary";
			case "Food":
				return "success";
			case "Room Rent":
				return "secondary";
			case "Bus Fare":
			case "Taxi Fare":
			case "Train Fare":
				return "info";
			case "Toll":
				return "warning";
			default:
				return "default";
		}
	};

	const calculateTotalAmount = () => {
		return filteredExpenses.reduce(
			(sum, expense) => sum + Number(expense.amount),
			0
		);
	};

	const hasActiveFilters = Object.values(filters).some(
		(value) => value !== "" && value !== null
	);

	const currentPageExpenses = filteredExpenses.slice(
		page * rowsPerPage,
		page * rowsPerPage + rowsPerPage
	);

	const allCurrentPageSelected =
		currentPageExpenses.length > 0 &&
		currentPageExpenses.every((exp) => selectedExpenses.has(exp.id));

	const someCurrentPageSelected = currentPageExpenses.some((exp) =>
		selectedExpenses.has(exp.id)
	);

	return (
		<LocalizationProvider dateAdapter={AdapterDayjs}>
			<Box sx={{ p: 2 }}>
				{/* Compact Header */}
				<Box
					sx={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
						mb: 2,
					}}>
					<Box>
						<Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
							Expense Approvals
						</Typography>
						<Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
							<Chip
								size="small"
								label={`${filteredExpenses.length} pending`}
								color="warning"
								variant="outlined"
							/>
							<Chip
								size="small"
								label={formatCurrency(calculateTotalAmount())}
								color="error"
								variant="outlined"
							/>
							{selectedExpenses.size > 0 && (
								<Chip
									size="small"
									label={`${selectedExpenses.size} selected`}
									color="primary"
								/>
							)}
						</Box>
					</Box>

					<Stack direction="row" spacing={1}>
						<Tooltip title="Refresh">
							<IconButton
								size="small"
								onClick={() => fetchPendingExpenses(true)}
								disabled={refreshing}>
								<RefreshIcon size={18} />
								{refreshing && (
									<CircularProgress size={18} sx={{ position: "absolute" }} />
								)}
							</IconButton>
						</Tooltip>
						<Button
							size="small"
							variant="outlined"
							startIcon={<SearchIcon size={16} />}
							onClick={() => setShowFilters(!showFilters)}
							color={
								hasActiveFilters || filters.searchTerm ? "primary" : "inherit"
							}>
							Search & Filter
							{(hasActiveFilters || filters.searchTerm) &&
								` (${
									Object.values(filters).filter((v) => v !== "" && v !== null)
										.length
								})`}
						</Button>
					</Stack>
				</Box>

				{/* Search and Filters Panel */}
				<Collapse in={showFilters}>
					<Paper sx={{ p: 1.5, mb: 2 }}>
						<Typography
							variant="subtitle2"
							sx={{
								mb: 1.5,
								display: "flex",
								alignItems: "center",
								fontSize: 13,
							}}>
							<SearchIcon size={16} style={{ marginRight: 8 }} />
							Search & Filter Options
						</Typography>

						{/* Search Bar */}
						<TextField
							fullWidth
							size="small"
							placeholder="Search by description, user name, or email..."
							value={filters.searchTerm}
							onChange={(e) => handleFilterChange("searchTerm", e.target.value)}
							sx={{ mb: 2 }}
						/>

						{/* Filter Options */}
						<Grid container spacing={1}>
							<Grid item xs={6} sm={3}>
								<DatePicker
									label="Start Date"
									value={filters.startDate}
									onChange={(date) => handleFilterChange("startDate", date)}
									slotProps={{ textField: { size: "small", fullWidth: true } }}
								/>
							</Grid>
							<Grid item xs={6} sm={3}>
								<DatePicker
									label="End Date"
									value={filters.endDate}
									onChange={(date) => handleFilterChange("endDate", date)}
									slotProps={{ textField: { size: "small", fullWidth: true } }}
								/>
							</Grid>
							<Grid item xs={6} sm={3}>
								<FormControl fullWidth size="small">
									<InputLabel>Type</InputLabel>
									<Select
										value={filters.expenseType}
										label="Type"
										onChange={(e) =>
											handleFilterChange("expenseType", e.target.value)
										}>
										<MenuItem value="">All</MenuItem>
										{expenseTypes.map((type) => (
											<MenuItem key={type.id} value={type.name}>
												{type.name}
											</MenuItem>
										))}
									</Select>
								</FormControl>
							</Grid>
							<Grid item xs={6} sm={3}>
								<Autocomplete
									options={[
										{ id: "", full_name: "All Users", email: "" },
										...users,
									]}
									getOptionLabel={(user) => {
										if (user.id === "") return "All Users";
										return user.full_name || user.email || "Unknown";
									}}
									value={
										users.find((user) => user.id === filters.user) || {
											id: "",
											full_name: "All Users",
											email: "",
										}
									}
									onChange={(event, newValue) => {
										handleFilterChange("user", newValue?.id || "");
									}}
									renderInput={(params) => (
										<TextField
											{...params}
											label="User"
											size="small"
											fullWidth
											placeholder="Search users..."
											InputProps={{
												...params.InputProps,
												startAdornment: (
													<SearchIcon
														size={16}
														style={{ marginRight: 4, color: "#666" }}
													/>
												),
											}}
										/>
									)}
									renderOption={(props, user) => (
										<Box
											component="li"
											{...props}
											sx={{
												display: "flex",
												alignItems: "center",
												gap: 1,
												"&:hover": { bgcolor: "action.hover" },
												py: 1,
											}}>
											{user.id === "" ? (
												<>
													<Avatar
														sx={{
															width: 24,
															height: 24,
															fontSize: 12,
															bgcolor: "grey.400",
														}}>
														A
													</Avatar>
													<Typography
														variant="body2"
														sx={{ fontSize: 12, fontWeight: 500 }}>
														All Users
													</Typography>
												</>
											) : (
												<>
													<Avatar sx={{ width: 24, height: 24, fontSize: 12 }}>
														{(user.full_name || user.email || "U")
															.charAt(0)
															.toUpperCase()}
													</Avatar>
													<Box sx={{ flexGrow: 1 }}>
														<Typography
															variant="body2"
															sx={{ fontSize: 12, fontWeight: 500 }}>
															{user.full_name || user.email || "Unknown"}
														</Typography>
														{user.email && user.full_name && (
															<Typography
																variant="caption"
																color="text.secondary"
																sx={{ fontSize: 10 }}>
																{user.email}
															</Typography>
														)}
													</Box>
												</>
											)}
										</Box>
									)}
									filterOptions={(options, { inputValue }) => {
										if (!inputValue) return options;

										// Always show "All Users" option first
										const allOption = options.find((opt) => opt.id === "");

										// Filter other options
										const filtered = options.filter((option) => {
											if (option.id === "") return true; // Always include "All Users"

											const name = (option.full_name || "").toLowerCase();
											const email = (option.email || "").toLowerCase();
											const search = inputValue.toLowerCase();
											return name.includes(search) || email.includes(search);
										});

										return filtered;
									}}
									isOptionEqualToValue={(option, value) =>
										option.id === value?.id
									}
									size="small"
									disableClearable
									openOnFocus
									selectOnFocus
									clearOnBlur
									handleHomeEndKeys
									noOptionsText="No users found"
									loadingText="Loading users..."
									componentsProps={{
										popper: {
											modifiers: [
												{
													name: "offset",
													options: {
														offset: [0, 4],
													},
												},
											],
										},
									}}
								/>
							</Grid>
						</Grid>

						{/* Action Buttons */}
						<Box
							sx={{
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
								mt: 2,
							}}>
							<Button size="small" onClick={resetFilters}>
								Reset All
							</Button>
							{selectedExpenses.size > 0 && (
								<ButtonGroup size="small" variant="contained">
									<Button
										color="success"
										onClick={handleBulkApprove}
										startIcon={<CheckIcon size={14} />}
										disabled={[...selectedExpenses].some((id) =>
											processingExpenses.has(id)
										)}>
										Approve ({selectedExpenses.size})
									</Button>
									<Button
										color="error"
										onClick={handleBulkReject}
										startIcon={<XIcon size={14} />}
										disabled={[...selectedExpenses].some((id) =>
											processingExpenses.has(id)
										)}>
										Reject
									</Button>
								</ButtonGroup>
							)}
						</Box>
					</Paper>
				</Collapse>

				{/* Compact Table */}
				<Paper sx={{ borderRadius: 1 }}>
					<TableContainer>
						<Table size="small" sx={{ "& .MuiTableCell-root": { py: 1 } }}>
							<TableHead>
								<TableRow sx={{ bgcolor: "grey.50" }}>
									<TableCell padding="checkbox" sx={{ width: 40 }}>
										<input
											type="checkbox"
											checked={allCurrentPageSelected}
											ref={(input) => {
												if (input)
													input.indeterminate =
														someCurrentPageSelected && !allCurrentPageSelected;
											}}
											onChange={toggleAllExpenses}
										/>
									</TableCell>
									<TableCell sx={{ width: 80 }}>Date</TableCell>
									<TableCell sx={{ width: 140 }}>User</TableCell>
									<TableCell>Description</TableCell>
									<TableCell sx={{ width: 100 }}>Type</TableCell>
									<TableCell align="right" sx={{ width: 80 }}>
										Amount
									</TableCell>
									<TableCell sx={{ width: 120 }}>School</TableCell>
									<TableCell sx={{ width: 50 }}>Receipt</TableCell>
									<TableCell sx={{ width: 120 }}>Actions</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{loading ? (
									<TableRow>
										<TableCell colSpan={9} align="center" sx={{ py: 4 }}>
											<CircularProgress size={24} />
										</TableCell>
									</TableRow>
								) : filteredExpenses.length === 0 ? (
									<TableRow>
										<TableCell colSpan={9} align="center" sx={{ py: 4 }}>
											<Typography color="text.secondary">
												🎉 No pending expenses found!
											</Typography>
										</TableCell>
									</TableRow>
								) : (
									currentPageExpenses.map((expense) => (
										<TableRow
											key={expense.id}
											sx={{
												"&:hover": { bgcolor: "grey.50" },
												bgcolor: selectedExpenses.has(expense.id)
													? "action.selected"
													: "inherit",
											}}>
											<TableCell padding="checkbox">
												<input
													type="checkbox"
													checked={selectedExpenses.has(expense.id)}
													onChange={() => toggleExpenseSelection(expense.id)}
												/>
											</TableCell>
											<TableCell>
												<Typography variant="caption" sx={{ display: "block" }}>
													{dayjs(expense.date).format("DD/MM")}
												</Typography>
												<Typography variant="caption" color="text.secondary">
													{dayjs(expense.date).format("YYYY")}
												</Typography>
											</TableCell>
											<TableCell>
												<Box
													sx={{
														display: "flex",
														alignItems: "center",
														gap: 1,
													}}>
													<Avatar sx={{ width: 24, height: 24, fontSize: 12 }}>
														{(
															expense.profiles?.full_name ||
															expense.profiles?.email ||
															"U"
														)
															.charAt(0)
															.toUpperCase()}
													</Avatar>
													<Box>
														<Typography
															variant="body2"
															sx={{
																fontSize: 12,
																fontWeight: 500,
																lineHeight: 1.2,
															}}>
															{expense.profiles?.full_name || "Unknown"}
														</Typography>
														{expense.profiles?.email && (
															<Typography
																variant="caption"
																color="text.secondary"
																sx={{ fontSize: 10 }}>
																{expense.profiles.email.split("@")[0]}
															</Typography>
														)}
													</Box>
												</Box>
											</TableCell>
											<TableCell>
												<Typography variant="body2" sx={{ fontSize: 12 }}>
													{expense.description}
												</Typography>
											</TableCell>
											<TableCell>
												<Chip
													label={expense.expense_type}
													size="small"
													color={getExpenseTypeChipColor(expense.expense_type)}
													variant="outlined"
													sx={{ fontSize: 10, height: 20 }}
												/>
												{expense.distance && (
													<Typography
														variant="caption"
														display="block"
														color="text.secondary"
														sx={{ fontSize: 10 }}>
														{expense.distance} KM
													</Typography>
												)}
											</TableCell>
											<TableCell align="right">
												<Typography
													variant="body2"
													fontWeight="medium"
													sx={{ fontSize: 12 }}>
													{formatCurrency(expense.amount)}
												</Typography>
											</TableCell>
											<TableCell>
												{expense.school_visits ? (
													<Chip
														size="small"
														label={
															expense.school_visits.schools?.name || "Unknown"
														}
														color="info"
														variant="outlined"
														sx={{ fontSize: 10, height: 18 }}
													/>
												) : expense.schools ? (
													<Chip
														size="small"
														label={expense.schools.name}
														color="default"
														variant="outlined"
														sx={{ fontSize: 10, height: 18 }}
													/>
												) : (
													<Typography variant="caption" color="text.secondary">
														-
													</Typography>
												)}
											</TableCell>
											<TableCell>
												{expense.receipt_url ? (
													<Tooltip title="View Receipt">
														<IconButton
															size="small"
															onClick={() => viewReceipt(expense)}>
															<ReceiptIcon size={14} />
														</IconButton>
													</Tooltip>
												) : (
													<Typography variant="caption" color="text.secondary">
														-
													</Typography>
												)}
											</TableCell>
											<TableCell>
												<Stack direction="row" spacing={0.5}>
													<Tooltip title="Message">
														<IconButton
															size="small"
															onClick={() => openMessageModal(expense)}>
															<MessageIcon size={14} />
														</IconButton>
													</Tooltip>
													<Tooltip title="Approve">
														<IconButton
															size="small"
															color="success"
															onClick={() => handleApprove(expense.id)}
															disabled={processingExpenses.has(expense.id)}>
															{processingExpenses.has(expense.id) ? (
																<CircularProgress size={14} />
															) : (
																<CheckIcon size={14} />
															)}
														</IconButton>
													</Tooltip>
													<Tooltip title="Reject">
														<IconButton
															size="small"
															color="error"
															onClick={() => handleReject(expense.id)}
															disabled={processingExpenses.has(expense.id)}>
															{processingExpenses.has(expense.id) ? (
																<CircularProgress size={14} />
															) : (
																<XIcon size={14} />
															)}
														</IconButton>
													</Tooltip>
												</Stack>
											</TableCell>
										</TableRow>
									))
								)}
							</TableBody>
						</Table>
					</TableContainer>

					<TablePagination
						component="div"
						count={filteredExpenses.length}
						page={page}
						onPageChange={(event, newPage) => setPage(newPage)}
						rowsPerPage={rowsPerPage}
						onRowsPerPageChange={(event) => {
							setRowsPerPage(parseInt(event.target.value, 10));
							setPage(0);
						}}
						rowsPerPageOptions={[10, 25, 50, 100]}
						labelRowsPerPage="Rows:"
						sx={{
							borderTop: 1,
							borderColor: "divider",
							"& .MuiTablePagination-toolbar": { minHeight: 48 },
						}}
					/>
				</Paper>

				{/* Receipt Preview Dialog */}
				<Dialog
					open={openReceiptDialog}
					onClose={closeReceiptDialog}
					maxWidth="md"
					fullWidth>
					<DialogTitle sx={{ pb: 1 }}>
						<Typography variant="h6">Receipt Preview</Typography>
						<Typography variant="body2" color="text.secondary">
							{selectedExpense?.description} •{" "}
							{selectedExpense?.profiles?.full_name ||
								selectedExpense?.profiles?.email}
						</Typography>
					</DialogTitle>
					<DialogContent>
						{selectedExpense?.receipt_url ? (
							selectedExpense.receipt_url.toLowerCase().endsWith(".pdf") ? (
								<Box sx={{ height: "70vh", width: "100%" }}>
									<iframe
										src={selectedExpense.receipt_url}
										style={{ width: "100%", height: "100%", border: "none" }}
										title="Receipt PDF"
									/>
								</Box>
							) : (
								<Box sx={{ textAlign: "center", py: 2 }}>
									<img
										src={selectedExpense.receipt_url}
										alt="Receipt"
										style={{
											maxWidth: "100%",
											maxHeight: "70vh",
											borderRadius: 8,
											boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
										}}
									/>
								</Box>
							)
						) : (
							<Typography color="text.secondary" align="center" sx={{ py: 4 }}>
								No receipt available
							</Typography>
						)}
					</DialogContent>
					<DialogActions>
						{selectedExpense?.receipt_url && (
							<Button
								startIcon={<DownloadIcon size={16} />}
								href={selectedExpense.receipt_url}
								target="_blank"
								rel="noopener">
								Download
							</Button>
						)}
						<Button onClick={closeReceiptDialog}>Close</Button>
						<ButtonGroup variant="contained" size="small">
							<Button
								color="success"
								onClick={async () => {
									await handleApprove(selectedExpense.id);
									closeReceiptDialog();
								}}
								startIcon={<CheckIcon size={16} />}
								disabled={processingExpenses.has(selectedExpense?.id)}>
								Approve
							</Button>
							<Button
								color="error"
								onClick={async () => {
									await handleReject(selectedExpense.id);
									closeReceiptDialog();
								}}
								startIcon={<XIcon size={16} />}
								disabled={processingExpenses.has(selectedExpense?.id)}>
								Reject
							</Button>
						</ButtonGroup>
					</DialogActions>
				</Dialog>

				{/* Send Message Dialog */}
				<Dialog
					open={openMessageDialog}
					onClose={closeMessageModal}
					maxWidth="sm"
					fullWidth>
					<DialogTitle sx={{ pb: 1 }}>
						<Typography variant="h6">Send Message</Typography>
						<Typography variant="body2" color="text.secondary">
							To:{" "}
							{selectedExpense?.profiles?.full_name ||
								selectedExpense?.profiles?.email}
						</Typography>
					</DialogTitle>
					<DialogContent dividers>
						<Stack spacing={2}>
							{/* Compact Expense Details Summary */}
							<Paper
								elevation={0}
								sx={{ p: 1.5, bgcolor: "grey.50", borderRadius: 1 }}>
								<Typography
									variant="subtitle2"
									gutterBottom
									sx={{ fontSize: 12 }}>
									Expense Details:
								</Typography>
								<Grid container spacing={1}>
									<Grid item xs={6}>
										<Typography variant="caption" color="text.secondary">
											<strong>Date:</strong>{" "}
											{dayjs(selectedExpense?.date).format("DD/MM/YYYY")}
										</Typography>
									</Grid>
									<Grid item xs={6}>
										<Typography variant="caption" color="text.secondary">
											<strong>Amount:</strong>{" "}
											{formatCurrency(selectedExpense?.amount || 0)}
										</Typography>
									</Grid>
									<Grid item xs={6}>
										<Typography variant="caption" color="text.secondary">
											<strong>Type:</strong> {selectedExpense?.expense_type}
										</Typography>
									</Grid>
									<Grid item xs={6}>
										<Typography variant="caption" color="text.secondary">
											<strong>Status:</strong> {selectedExpense?.status}
										</Typography>
									</Grid>
									<Grid item xs={12}>
										<Typography variant="caption" color="text.secondary">
											<strong>Description:</strong>{" "}
											{selectedExpense?.description}
										</Typography>
									</Grid>
								</Grid>
							</Paper>

							<TextField
								label="Subject"
								value={messageForm.subject}
								onChange={(e) =>
									setMessageForm({ ...messageForm, subject: e.target.value })
								}
								fullWidth
								size="small"
								required
								error={!messageForm.subject.trim()}
								helperText={
									!messageForm.subject.trim() ? "Subject is required" : ""
								}
							/>

							<TextField
								label="Message"
								value={messageForm.message}
								onChange={(e) =>
									setMessageForm({ ...messageForm, message: e.target.value })
								}
								fullWidth
								multiline
								rows={3}
								size="small"
								required
								error={!messageForm.message.trim()}
								helperText={
									!messageForm.message.trim() ? "Message is required" : ""
								}
								placeholder="Please provide clarification or additional information needed for this expense..."
							/>
						</Stack>
					</DialogContent>
					<DialogActions>
						<Button
							onClick={closeMessageModal}
							disabled={sendingMessage}
							size="small">
							Cancel
						</Button>
						<Button
							onClick={handleSendMessage}
							variant="contained"
							size="small"
							disabled={
								!messageForm.subject.trim() ||
								!messageForm.message.trim() ||
								sendingMessage
							}
							startIcon={
								sendingMessage ? (
									<CircularProgress size={14} />
								) : (
									<SendIcon size={14} />
								)
							}>
							{sendingMessage ? "Sending..." : "Send"}
						</Button>
					</DialogActions>
				</Dialog>

				{/* Notification Snackbar */}
				<Snackbar
					open={notification.open}
					autoHideDuration={4000}
					onClose={hideNotification}
					anchorOrigin={{ vertical: "bottom", horizontal: "left" }}>
					<Alert
						onClose={hideNotification}
						severity={notification.severity}
						variant="filled"
						sx={{ width: "100%" }}>
						{notification.message}
					</Alert>
				</Snackbar>
			</Box>
		</LocalizationProvider>
	);
};

export default ExpenseApprovals;
